import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('urban-chat-input')
export class ChatInput extends LitElement {
  static override styles = css`
    :host {
      display: block;
      border-top: 1px solid var(--urban-border, #E5E7EB);
      background: var(--urban-bg-soft, #1a1a1a);
      padding: 12px 14px;
    }
    form {
      display: flex;
      gap: 8px;
      align-items: flex-end;
    }
    textarea {
      flex: 1;
      resize: none;
      border: 1px solid var(--urban-border, #262626);
      border-radius: 6px;
      padding: 10px 12px;
      font: inherit;
      font-size: 14px;
      line-height: 1.4;
      max-height: 120px;
      outline: none;
      background: var(--urban-bg, #0f0f0f);
      color: var(--urban-heading, #ffffff);
      caret-color: var(--urban-accent, #ff650c);
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    textarea:focus {
      border-color: var(--urban-accent, #ff650c);
      box-shadow: 0 0 0 3px var(--urban-accent-soft, rgba(255, 101, 12, 0.16));
    }
    textarea::placeholder { color: var(--urban-muted, #7a7a7a); }
    textarea:disabled {
      overflow: hidden;
      cursor: not-allowed;
      opacity: 0.6;
      background: var(--urban-bg-soft, #1a1a1a);
    }
    /* iOS Safari auto-zooms the page when a focused input is under 16px. On
       phones (<=600px, where the panel is full-screen) bump the composer to
       16px so tapping it does not zoom the page; desktop keeps the 14px design. */
    @media (max-width: 600px) {
      textarea { font-size: 16px; }
    }
    button {
      background: var(--urban-accent, #ff650c);
      color: #0a0a0a;
      border: 2px solid var(--urban-accent, #ff650c);
      border-radius: 6px;
      padding: 9px 16px;
      font: inherit;
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    button:not(:disabled):hover {
      background: #ffffff;
      border-color: #ffffff;
    }
    button:disabled {
      background: #2a2a2a;
      border-color: #2a2a2a;
      color: var(--urban-muted, #7a7a7a);
      cursor: not-allowed;
    }
  `;

  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) placeholder = 'Scrie un mesaj…';
  @state() private value = '';

  override updated(changed: Map<string, unknown>) {
    if (changed.has('disabled') && !this.disabled) {
      const ta = this.renderRoot.querySelector('textarea') as HTMLTextAreaElement | null;
      ta?.focus();
    }
  }

  private handleInput(e: Event) {
    const t = e.target as HTMLTextAreaElement;
    this.value = t.value;
    t.style.height = 'auto';
    t.style.height = `${Math.min(t.scrollHeight, 120)}px`;
  }

  private handleKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submit();
    }
  }

  private submit() {
    const text = this.value.trim();
    if (!text || this.disabled) return;
    this.dispatchEvent(new CustomEvent('send', { detail: { text }, bubbles: true, composed: true }));
    this.value = '';
    const ta = this.renderRoot.querySelector('textarea');
    if (ta) ta.style.height = 'auto';
  }

  override render() {
    return html`
      <form @submit=${(e: Event) => { e.preventDefault(); this.submit(); }}>
        <textarea
          rows="1"
          .value=${this.value}
          ?disabled=${this.disabled}
          placeholder=${this.placeholder}
          @input=${this.handleInput}
          @keydown=${this.handleKey}
        ></textarea>
        <button type="submit" ?disabled=${this.disabled || !this.value.trim()}>Trimite</button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'urban-chat-input': ChatInput;
  }
}
